import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StartScreen.css';
import logo from '../assets/logo.svg';
import { employeeApi, getErrorMessage } from '../utils/api';
import { validateEmployeeIdWithData } from '../utils/validation';
import { isAuthenticated, getUserData } from '../utils/auth';
import { processLocation } from '../utils/locationUtils';
import { ROUTES } from '../constants/app';
import WebGLBackground from './WebGLBackground';
import VirtualKeyboard from './VirtualKeyboard';

function StartScreen() {
  const [employeeId, setEmployeeId] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState('');
  
  // Virtual Keyboard state
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardMode, setKeyboardMode] = useState('numeric');
  const [activeInputIndex, setActiveInputIndex] = useState(null);
  const [isUppercase, setIsUppercase] = useState(false);
  
  const navigate = useNavigate();

  // Debounce search to filter local employee data
  useEffect(() => {
    if (!showSearchModal || !searchName.trim()) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    // Only search if 3 or more characters are entered
    if (searchName.trim().length < 3) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    const timeoutId = setTimeout(() => {
      try {
        setSearchLoading(true);
        setSearchError('');
        
        // Filter local employee data
        const searchTerm = searchName.trim().toLowerCase();
        const filteredEmployees = allEmployees.filter(employee => {
          const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
          return fullName.includes(searchTerm) || 
                 employee.first_name.toLowerCase().includes(searchTerm) ||
                 employee.last_name.toLowerCase().includes(searchTerm);
        });
        
        setSearchResults(filteredEmployees);
        if (filteredEmployees.length === 0) {
          setSearchError('No employees found with that name');
        }
      } catch (error) {
        console.error('Error searching employees:', error);
        setSearchError('Search failed. Please try again.');
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchName, showSearchModal, allEmployees]);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      // Redirect to login if not authenticated
      navigate(ROUTES.LOGIN);
      return;
    }
    
    const userData = getUserData();
    if (userData) {
      setCurrentUser(userData);
    } else {
      navigate(ROUTES.LOGIN);
    }
  }, [navigate]);

  // Fetch all employee details on component load
  useEffect(() => {
    const fetchAllEmployees = async () => {
      if (!currentUser) return; // Only fetch if user is authenticated
      
      try {
        setEmployeesLoading(true);
        setEmployeesError('');
        
        const response = await employeeApi.getAllDetails();
        
        if (response.success && response.data) {
          setAllEmployees(response.data);
        } else {
          setEmployeesError('Failed to load employee details');
        }
      } catch (error) {
        console.error('Error fetching all employee details:', error);
        setEmployeesError(getErrorMessage(error));
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchAllEmployees();
  }, [currentUser]);

  const handleInputChange = (index, value) => {
    // Only allow single digits
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newEmployeeId = [...employeeId];
      newEmployeeId[index] = value;
      setEmployeeId(newEmployeeId);
      
      // Clear error when user starts typing
      if (error) setError('');
      
      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`digit-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && !employeeId[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleGo = () => {
    const fullEmployeeId = employeeId.join('');
    
    // Check if employee data is still loading
    if (employeesLoading) {
      setError('Loading employee data, please wait...');
      return;
    }
    
    // Check if there was an error loading employee data
    if (employeesError) {
      setError('Unable to validate Employee ID. Please try again.');
      return;
    }
    
    // Validate employee ID against local data
    const validation = validateEmployeeIdWithData(fullEmployeeId, allEmployees);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }
    
    // Navigate directly with the employee data from local validation
    navigate(`${ROUTES.ID_CARD}/${validation.value}`, { 
      state: { 
        employeeData: validation.employeeData,
        originalId: validation.value 
      } 
    });
  };

  const handleSearchByName = () => {
    setShowSearchModal(true);
    setSearchName('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleCloseModal = () => {
    setShowSearchModal(false);
    setSearchName('');
    setSearchResults([]);
    setSearchError('');
    setShowKeyboard(false);
    setActiveInputIndex(null);
  };

  const handleSelectEmployee = (employee) => {
    setShowSearchModal(false);
    // Navigate to IDCard with employee data
    navigate(`${ROUTES.ID_CARD}/${employee.empId}`, { 
      state: { 
        employeeData: employee,
        originalId: employee.empId 
      } 
    });
  };

  // Virtual Keyboard handlers
  const handleEmployeeIdFocus = (index) => {
    setActiveInputIndex(index);
    setKeyboardMode('numeric');
    setShowKeyboard(true);
  };

  const handleSearchInputFocus = () => {
    setKeyboardMode('text');
    setShowKeyboard(true);
    setIsUppercase(false);
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
    setActiveInputIndex(null);
  };

  const handleKeyboardKeyPress = (key) => {
    if (keyboardMode === 'numeric') {
      handleNumericKeyPress(key);
    } else {
      handleTextKeyPress(key);
    }
  };

  const handleNumericKeyPress = (key) => {
    if (activeInputIndex === null) return;

    if (key === 'Backspace') {
      // Clear current digit and move to previous
      const newEmployeeId = [...employeeId];
      if (newEmployeeId[activeInputIndex]) {
        newEmployeeId[activeInputIndex] = '';
        setEmployeeId(newEmployeeId);
      } else if (activeInputIndex > 0) {
        const prevIndex = activeInputIndex - 1;
        newEmployeeId[prevIndex] = '';
        setEmployeeId(newEmployeeId);
        setActiveInputIndex(prevIndex);
        // Focus previous input
        setTimeout(() => {
          const prevInput = document.getElementById(`digit-${prevIndex}`);
          if (prevInput) prevInput.focus();
        }, 0);
      }
    } else if (key === 'Clear') {
      // Clear all digits
      setEmployeeId(['', '', '', '']);
      setActiveInputIndex(0);
      setTimeout(() => {
        const firstInput = document.getElementById('digit-0');
        if (firstInput) firstInput.focus();
      }, 0);
    } else if (/^[0-9]$/.test(key)) {
      // Handle number input
      const newEmployeeId = [...employeeId];
      newEmployeeId[activeInputIndex] = key;
      setEmployeeId(newEmployeeId);
      
      // Clear error when user starts typing
      if (error) setError('');
      
      // Auto-advance to next input
      if (activeInputIndex < 3) {
        const nextIndex = activeInputIndex + 1;
        setActiveInputIndex(nextIndex);
        setTimeout(() => {
          const nextInput = document.getElementById(`digit-${nextIndex}`);
          if (nextInput) nextInput.focus();
        }, 0);
      }
    }
  };

  const handleTextKeyPress = (key) => {
    if (key === 'Backspace') {
      if (searchName.length > 0) {
        setSearchName(searchName.slice(0, -1));
      }
    } else if (key.length === 1) {
      // Regular character
      setSearchName(searchName + key);
    }
  };

  const handleToggleCase = () => {
    setIsUppercase(!isUppercase);
  };

  // Don't render anything if user is not authenticated (will redirect)
  if (!currentUser) {
    return null;
  }

  return (
    <div className={`start-screen ${showKeyboard ? 'keyboard-active' : ''}`}>
      <WebGLBackground 
        height="100vh" 
        width="100%" 
        left="0" 
        opacity={0.8}
        zIndex={-1}
      />

      <div className="start-screen-content">
        <div className="logo-section">
          <div className="logo">
            <img src={logo} alt="Exchange Logo" className="logo-image" />
          </div>
        </div>
        
        <div className="content-section">
        <h1 className="main-title">Your Exchange journey starts here</h1>
        <p className="subtitle">Enter your Employee ID to get started</p>
        
        <div className="employee-id-input">
          {employeeId.map((digit, index) => (
            <input
              key={index}
              id={`digit-${index}`}
              type="text"
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => handleEmployeeIdFocus(index)}
              className="digit-input"
              maxLength="1"
              disabled={employeesLoading}
            />
          ))}
        </div>

      
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <button 
          className="go-button" 
          onClick={handleGo} 
          disabled={employeesLoading || employeeId.some(digit => digit === '')}
        >
          {employeesLoading ? 'Loading employee data...' : 'Go'}
        </button>

        <div className="search-by-name-section">
          <span>(OR) </span>
          <button className="search-by-name-link" onClick={handleSearchByName} disabled={employeesLoading}>
            Search by Name
          </button>
        </div>
        </div>
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Search Employee by Name</h2>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="search-input-section">
                <input
                  type="text"
                  placeholder="Enter at least 3 characters to search..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onFocus={handleSearchInputFocus}
                  className="search-input"
                  disabled={searchLoading}
                  autoFocus
                />
                {searchLoading && (
                  <div className="search-loading">
                    Searching...
                  </div>
                )}
              </div>

              {searchError && (
                <div className="search-error-message">
                  {searchError}
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="search-results">
                  <div className="results-list">
                    {searchResults.map((employee) => (
                      <div 
                        key={employee.empId} 
                        className="result-item"
                        onClick={() => handleSelectEmployee(employee)}
                      >
                        <div className="result-name">
                          {employee.first_name} {employee.last_name}
                        </div>
                        <div className="result-details">
                          {employee.emp_id} | {processLocation(employee.location)} | {employee.department}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Virtual Keyboard */}
      <VirtualKeyboard
        show={showKeyboard}
        mode={keyboardMode}
        onKeyPress={handleKeyboardKeyPress}
        onClose={handleKeyboardClose}
        isUppercase={isUppercase}
        onToggleCase={handleToggleCase}
      />
    </div>
  );
}

export default StartScreen;
